import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Typography,
  Select,
  Input,
  Space,
  Avatar,
  Tag,
  Statistic,
  Spin,
  Alert,
  Modal,
  Button,
  Divider,
  Empty,
  message,
} from "antd";

import {
  TrophyOutlined,
  CrownOutlined,
  TeamOutlined,
  SearchOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  BarChartOutlined,
  RiseOutlined,
  StarFilled,
} from "@ant-design/icons";

import { PDFDownloadLink } from "@react-pdf/renderer";
import { useAcademicYear } from "../../AcademicYearContext";
import api from "../../api/api";
import TopStudentPerPeriodPDF from "./TopStudentPerPeriodPDF";

const { Title, Text } = Typography;
const { Option } = Select;

const TopStudentPerPeriod = () => {
  const { selectedYear } = useAcademicYear();

  const [gradeClasses, setGradeClasses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [students, setStudents] = useState([]);
  const [reportData, setReportData] = useState(null);

  const [selectedClass, setSelectedClass] = useState();
  const [selectedPeriod, setSelectedPeriod] = useState();

  const [loading, setLoading] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(false);

  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const [showPDFModal, setShowPDFModal] = useState(false);

  const loadGradeClasses = useCallback(async () => {
    try {
      setLoadingClasses(true);

      const response = await api.get("/api/users/grade-classes/");

      setGradeClasses(response.data.results || response.data);
    } catch (error) {
      console.error(error);
      message.error("Unable to load grade classes.");
    } finally {
      setLoadingClasses(false);
    }
  }, []);

  const loadPeriods = useCallback(async () => {
    try {
      setLoadingPeriods(true);

      const response = await api.get("/api/users/periods/");

      setPeriods(response.data.results || response.data);
    } catch (error) {
      console.error(error);
      message.error("Unable to load academic periods.");
    } finally {
      setLoadingPeriods(false);
    }
  }, []);

  const loadTopStudents = useCallback(async () => {
    if (!selectedClass || !selectedPeriod) {
      setStudents([]);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await api.get("/top-students/", {
        params: {
          grade_class: selectedClass,
          period: selectedPeriod,
          search,
        },
      });

      const data = response.data;

      setStudents(data.results || []);

      setReportData({
        academic_year: data.academic_year,
        grade_class:
          data.results?.length > 0
            ? data.results[0].grade_class
            : "",
        period:
          data.results?.length > 0
            ? data.results[0].period
            : "",
        students: data.results || [],
      });
    } catch (error) {
      console.error(error);
      setStudents([]);
      setError("Unable to load top students.");
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedPeriod, search]);

  useEffect(() => {
    loadGradeClasses();
    loadPeriods();
  }, [loadGradeClasses, loadPeriods]);

  useEffect(() => {
    loadTopStudents();
  }, [loadTopStudents]);

  const handleGradeClassChange = (value) => {
    setSelectedClass(value);
  };

  const handlePeriodChange = (value) => {
    setSelectedPeriod(value);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleDownloadReport = () => {
    if (!students.length) {
      message.warning("No student rankings available.");
      return;
    }

    setShowPDFModal(true);
  };


  const dux = students.length ? students[0] : null;

  const secondPlace = students.length > 1 ? students[1] : null;

  const thirdPlace = students.length > 2 ? students[2] : null;

  return (
    <div
      style={{
        padding: 24,
        background: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <Card
        bordered={false}
        style={{
          borderRadius: 16,
          marginBottom: 24,
          background: "linear-gradient(135deg,#800000,#4b0000)",
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={2}>
              <Title
                level={2}
                style={{
                  color: "#ffffff",
                  margin: 0,
                }}
              >
                <TrophyOutlined /> Top Students Per Period
              </Title>

              <Text
                style={{
                  color: "#f5f5f5",
                  fontSize: 15,
                }}
              >
                Academic Year:{" "}
                <strong>
                  {selectedYear?.name || "Loading..."}
                </strong>
              </Text>

              <Text
                style={{
                  color: "#dddddd",
                }}
              >
                View class rankings, identify the Dux and generate professional reports.
              </Text>
            </Space>
          </Col>

          <Col>
            <Avatar
              size={90}
              style={{
                background: "#b8860b",
              }}
              icon={<TrophyOutlined />}
            />
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Class Dux"
              value={dux ? dux.student_name : "--"}
              prefix={<CrownOutlined style={{ color: "#b8860b" }} />}
              valueStyle={{
                color: "#800000",
                fontSize: 18,
              }}
            />

            <Divider style={{ margin: "12px 0" }} />

            <Tag color="gold">
              {dux ? `${dux.average_score}%` : "No Data"}
            </Tag>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Students Ranked"
              value={students.length}
              prefix={<TeamOutlined style={{ color: "#800000" }} />}
              valueStyle={{
                color: "#800000",
              }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Selected Class"
              value={
                gradeClasses.find(
                  (c) => c.id === selectedClass
                )?.name || "--"
              }
              prefix={<BarChartOutlined style={{ color: "#800000" }} />}
              valueStyle={{
                color: "#800000",
              }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Report Status"
              value={students.length ? "Ready" : "Waiting"}
              prefix={<RiseOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{
                color: students.length ? "#52c41a" : "#fa8c16",
              }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        bordered={false}
        style={{
          borderRadius: 16,
          marginBottom: 24,
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Text strong>Grade Class</Text>

            <Select
              showSearch
              placeholder="Select Grade Class"
              value={selectedClass}
              loading={loadingClasses}
              onChange={handleGradeClassChange}
              style={{
                width: "100%",
                marginTop: 8,
              }}
            >
              {gradeClasses.map((item) => (
                <Option
                  key={item.id}
                  value={item.id}
                >
                  {item.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={8}>
            <Text strong>Academic Period</Text>

            <Select
              showSearch
              placeholder="Select Period"
              value={selectedPeriod}
              loading={loadingPeriods}
              onChange={handlePeriodChange}
              style={{
                width: "100%",
                marginTop: 8,
              }}
            >
              {periods.map((item) => (
                <Option
                  key={item.id}
                  value={item.id}
                >
                  {item.name}
                </Option>
              ))}
            </Select>
          </Col>

          {/* <Col xs={24} md={8}>
            <Text strong>Search Student</Text>

            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search student..."
              value={search}
              onChange={handleSearch}
              style={{
                marginTop: 8,
              }}
            />
          </Col> */}
        </Row>
      </Card>

      {students.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 15,
                textAlign: "center",
              }}
            >
              <Avatar
                size={65}
                style={{
                  background: "#c0c0c0",
                  marginBottom: 10,
                }}
              >
                2
              </Avatar>

              <Title level={5}>
                {secondPlace?.student_name || "--"}
              </Title>

              <Tag color="default">
                {secondPlace?.average_score || "--"}%
              </Tag>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 15,
                textAlign: "center",
                border: "2px solid #b8860b",
              }}
            >
              <Avatar
                size={85}
                style={{
                  background: "#b8860b",
                  marginBottom: 10,
                }}
                icon={<CrownOutlined />}
              />

              <Title
                level={4}
                style={{
                  color: "#800000",
                }}
              >
                {dux?.student_name || "--"}
              </Title>

              <Tag color="gold">
                {dux?.average_score || "--"}%
              </Tag>

              <br />

              <Tag color="red">
                Class Dux
              </Tag>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 15,
                textAlign: "center",
              }}
            >
              <Avatar
                size={65}
                style={{
                  background: "#8b4513",
                  marginBottom: 10,
                }}
              >
                3
              </Avatar>

              <Title level={5}>
                {thirdPlace?.student_name || "--"}
              </Title>

              <Tag color="orange">
                {thirdPlace?.average_score || "--"}%
              </Tag>
            </Card>
          </Col>
        </Row>
      )}

      <Card
        bordered={false}
        title={
          <Space>
            <StarFilled style={{ color: "#b8860b" }} />
            Student Rankings
          </Space>
        }
        extra={
          <Button
            danger
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadReport}
            disabled={!students.length}
          >
            Download PDF
          </Button>
        }
        style={{
          borderRadius: 16,
        }}
      >
        {loading ? (
          <Spin size="large" />
        ) : error ? (
          <Alert
            type="error"
            message={error}
          />
        ) : students.length === 0 ? (
          <Empty description="No ranking data available." />
        ) : (
          <Table
            columns={[
              {
                title: "Rank",
                dataIndex: "rank",
                key: "rank",
                width: 90,
                align: "center",
                render: (rank) => (
                  <Avatar
                    style={{
                      background:
                        rank === 1
                          ? "#b8860b"
                          : rank === 2
                          ? "#808080"
                          : rank === 3
                          ? "#8b4513"
                          : "#800000",
                    }}
                  >
                    {rank}
                  </Avatar>
                ),
              },
              {
                title: "Student Name",
                dataIndex: "student_name",
                key: "student_name",
                render: (name) => (
                  <Space>
                    <Avatar
                      style={{
                        background: "#800000",
                      }}
                    >
                      {name?.charAt(0)}
                    </Avatar>

                    <Text strong>{name}</Text>
                  </Space>
                ),
              },
              {
                title: "Grade Class",
                dataIndex: "grade_class",
                key: "grade_class",
                align: "center",
                render: (value) => (
                  <Tag color="volcano">
                    {value}
                  </Tag>
                ),
              },
              {
                title: "Period",
                dataIndex: "period",
                key: "period",
                align: "center",
                render: (value) => (
                  <Tag color="blue">
                    {value}
                  </Tag>
                ),
              },
              {
                title: "Average",
                dataIndex: "average_score",
                key: "average_score",
                align: "center",
                sorter: (a, b) =>
                  a.average_score - b.average_score,
                render: (value) => (
                  <Tag
                    color={
                      value >= 95
                        ? "green"
                        : value >= 90
                        ? "blue"
                        : value >= 80
                        ? "gold"
                        : "orange"
                    }
                  >
                    {value}%
                  </Tag>
                ),
              },
              {
                title: "Grade",
                dataIndex: "alphabet_grade",
                key: "alphabet_grade",
                align: "center",
                render: (grade) => (
                  <Tag
                    color={
                      grade === "A+"
                        ? "green"
                        : grade === "A"
                        ? "blue"
                        : grade === "B+"
                        ? "cyan"
                        : grade === "B"
                        ? "gold"
                        : "orange"
                    }
                  >
                    {grade}
                  </Tag>
                ),
              },
            ]}
            dataSource={students}
            rowKey="student_id"
            bordered
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) =>
                `Total Ranked Students: ${total}`,
            }}
          />
        )}
      </Card>

      <Modal
        title={
          <Space>
            <FilePdfOutlined />
            Download Top Students Report
          </Space>
        }
        open={showPDFModal}
        onCancel={() => setShowPDFModal(false)}
        footer={null}
        centered
      >
        <Text>
          Generate and download the official Top Students Per Period report.
        </Text>

        <Divider />

        <Space>
          <PDFDownloadLink
            document={
              <TopStudentPerPeriodPDF
                data={reportData}
              />
            }
            fileName={`Top_Students_${reportData?.grade_class}_${reportData?.period}.pdf`}
          >
            {({ loading }) => (
              <Button
                type="primary"
                danger
                icon={<DownloadOutlined />}
                loading={loading}
              >
                Download PDF
              </Button>
            )}
          </PDFDownloadLink>

          <Button
            onClick={() => setShowPDFModal(false)}
          >
            Cancel
          </Button>
        </Space>
      </Modal>
    </div>
  );
};

export default TopStudentPerPeriod;